import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { SEO } from "../components/SEO";
import { SITE } from "../data/site";

const LegalPage = ({ title, description, path, updated, sections }) => (
    <>
        <SEO title={`${title} — ${SITE.name}`} description={description} path={path} />
        <Navbar />
        <main className="mx-auto max-w-3xl px-4 pb-24 pt-32 sm:px-8 sm:pt-40" data-testid={`legal-${path.slice(1)}`}>
            <Link
                to="/"
                data-testid="legal-back-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-brand-orange transition-colors duration-200 hover:text-brand-orange-dark"
            >
                <ArrowLeft size={15} aria-hidden="true" />
                Back to home
            </Link>
            <h1 className="mt-8 font-display text-3xl font-bold tracking-tight text-brand-brown sm:text-4xl">{title}</h1>
            <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-brand-muted">Last updated: {updated}</p>
            <div className="mt-10 space-y-10">
                {sections.map((s) => (
                    <section key={s.h}>
                        <h2 className="font-display text-xl font-bold text-brand-brown">{s.h}</h2>
                        {s.paragraphs.map((p, i) => (
                            <p key={i} className="mt-3 text-sm leading-relaxed text-brand-muted sm:text-base">
                                {p}
                            </p>
                        ))}
                    </section>
                ))}
            </div>
            <p className="mt-12 rounded-xl border border-brand-brown/10 bg-brand-cream p-5 text-xs leading-relaxed text-brand-muted">
                Questions about this page? Email {SITE.email} or WhatsApp {SITE.whatsappDisplay}.
            </p>
        </main>
        <Footer />
    </>
);

const UPDATED = "July 2026";

export function PrivacyPage() {
    return (
        <LegalPage
            title="Privacy Policy"
            description={`How ${SITE.name} handles the small amount of information you share with us.`}
            path="/privacy"
            updated={UPDATED}
            sections={[
                {
                    h: "What we collect",
                    paragraphs: [
                        "We collect only what you choose to share: your name, business name, phone or WhatsApp number, business type and the message you write when you request a business scan or contact us. That's it. We don't collect passwords, payment details or sensitive personal information.",
                        "This website has no accounts, no tracking cookies set by us, and no analytics that profile individual visitors.",
                    ],
                },
                {
                    h: "How the contact form works",
                    paragraphs: [
                        "Our business scan form does not store your details on this website or in any database we run. When you submit it, your details are used once — to compose a pre-filled WhatsApp message that opens on your own device. From that point, your message is handled by WhatsApp under WhatsApp's own privacy policy.",
                    ],
                },
                {
                    h: "How we use your information",
                    paragraphs: [
                        "Details you send us by WhatsApp or email are used to respond to you, prepare your business scan, and — if you become a client — to deliver the work. We don't sell, rent or share your information with third parties for their marketing.",
                    ],
                },
                {
                    h: "Third-party platforms",
                    paragraphs: [
                        "We work with platforms like Google, WhatsApp and others on your behalf when delivering services. Those platforms have their own privacy policies, and we encourage you to review them.",
                    ],
                },
                {
                    h: "Your choices",
                    paragraphs: [
                        `You can ask us at any time what information we hold about you, correct it, or ask us to delete it. Email ${SITE.email} and we'll take care of it.`,
                    ],
                },
            ]}
        />
    );
}

export function TermsPage() {
    return (
        <LegalPage
            title="Terms of Service"
            description={`The terms that apply when you use the ${SITE.name} website and services.`}
            path="/terms"
            updated={UPDATED}
            sections={[
                {
                    h: "Using this website",
                    paragraphs: [
                        "This website explains what 033 Digital does and lets you contact us. By using it, you agree not to misuse it — for example by attempting to disrupt it, scrape it at abusive volumes, or submit false or malicious information through our forms.",
                    ],
                },
                {
                    h: "Services and scope",
                    paragraphs: [
                        "Specific services — such as Google Business Profile optimisation, website work, WhatsApp automation, content support or AI-assisted workflows — are agreed individually with each client in writing before work begins. This website describes what we offer; it is not itself a contract.",
                    ],
                },
                {
                    h: "No guaranteed outcomes",
                    paragraphs: [
                        "Marketing outcomes depend on many factors outside anyone's control — competition, market conditions, and platforms like Google and Meta that change their rules and algorithms. We do not guarantee search rankings, lead volumes, review counts, booking numbers or revenue figures, and you should treat anyone who does with caution.",
                        "What we commit to is the work: building the system properly, implementing it carefully, measuring honestly and improving it continuously.",
                    ],
                },
                {
                    h: "AI-assisted content",
                    paragraphs: [
                        "Some content and workflows we deliver are produced with AI assistance and reviewed by a human before use. AI-assisted material is checked for accuracy and tone, but final responsibility for publishing content about your business rests with the business owner.",
                    ],
                },
                {
                    h: "WhatsApp and third-party platforms",
                    paragraphs: [
                        "Where we set up automation or communication flows on third-party platforms (such as WhatsApp), those platforms' own terms apply. Platform features, pricing and policies can change; we'll always tell you if a change materially affects work we've built for you.",
                    ],
                },
                {
                    h: "Content you share with us",
                    paragraphs: [
                        "When you share photos, videos or customer material (including user-generated content) for use in marketing, you confirm you have the right to use it. Where UGC features your customers, appropriate consent and disclosure remain your responsibility as the business owner, and we'll flag this where relevant.",
                    ],
                },
                {
                    h: "Liability",
                    paragraphs: [
                        "We work carefully and in good faith, but to the extent permitted by law, 033 Digital is not liable for indirect losses — such as lost profits — arising from use of this website or reliance on general information published here.",
                    ],
                },
            ]}
        />
    );
}

export function DisclaimerPage() {
    return (
        <LegalPage
            title="Disclaimer"
            description={`Important context about the claims and content on the ${SITE.name} website.`}
            path="/disclaimer"
            updated={UPDATED}
            sections={[
                {
                    h: "No fabricated proof",
                    paragraphs: [
                        "This website does not display invented testimonials, fake client logos, made-up ratings or fabricated performance numbers. Where results and case studies appear in future, they will reflect real clients with their approval.",
                    ],
                },
                {
                    h: "Marketing outcomes vary",
                    paragraphs: [
                        "Nothing on this website is a promise of specific results. Search rankings, lead volumes, review counts, bookings and revenue depend on your market, competition, pricing, service quality and third-party platforms. Our role is systems, strategy, implementation and optimisation — outcomes follow from consistent work, not guarantees.",
                    ],
                },
                {
                    h: "No platform affiliation",
                    paragraphs: [
                        "033 Digital is an independent studio. We are not affiliated with, endorsed by or partnered with Google, Meta, WhatsApp or YouTube. References to these platforms describe where we implement work for clients, nothing more.",
                    ],
                },
                {
                    h: "General information only",
                    paragraphs: [
                        "Content on this site is general marketing information, not professional, legal or financial advice for your specific situation. Decisions about your business should consider your own circumstances.",
                    ],
                },
                {
                    h: "External links",
                    paragraphs: [
                        "This site links to third-party services such as WhatsApp. We're not responsible for the content, availability or policies of external sites and platforms.",
                    ],
                },
            ]}
        />
    );
}
